import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EchoService {
  private echo: any = null;
  private pollingInterval: any = null;

  // Cache für bereits gezeigte/abgewiesene Anrufe: callId -> timestamp
  private suppressedCallIds: Map<string, number> = new Map();
  private SUPPRESS_TTL_MS = 60_000; // 60 Sekunden Unterdrückung

  constructor() {
    this.initializeEcho();
  }

  private initializeEcho() {
    try {
      // Erstelle ein Echo-ähnliches Objekt für Kompatibilität
      this.echo = {
        channel: (channelName: string) => ({
          listen: (eventName: string, callback: Function) => {
            console.log(`Höre auf Event ${eventName} im Kanal ${channelName}`);
            
            // Starte Polling für eingehende Anrufe
            this.startPolling(callback);
            
            return this;
          }
        }),
        leave: (channelName: string) => {
          console.log(`Verlasse Kanal ${channelName}`);
          this.stopPolling();
        }
      };
      
      console.log('Echo-Service für eingehende Anrufe initialisiert (Polling-Modus)');
    } catch (error) {
      console.error('Fehler beim Initialisieren des Echo-Service:', error);
      this.echo = null;
    }
  }

  private startPolling(callback: Function) {
    // Wenn Telephony deaktiviert ist, kein Polling starten
    if (!environment.telephonyEnabled) {
      console.log('Telephony deaktiviert - Polling wird nicht gestartet');
      return;
    }
    
    // Stoppe vorheriges Polling falls vorhanden
    this.stopPolling();
    
    // Polling alle 5 Sekunden
    this.pollingInterval = setInterval(() => {
      this.checkForIncomingCalls(callback);
    }, 5000);
    
    console.log('Polling für eingehende Anrufe gestartet (alle 5 Sekunden)');
  }

  private stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      console.log('Polling für eingehende Anrufe gestoppt');
    }
  }

  private pruneSuppressed() {
    const now = Date.now();
    for (const [id, ts] of Array.from(this.suppressedCallIds.entries())) {
      if (now - ts > this.SUPPRESS_TTL_MS) {
        this.suppressedCallIds.delete(id);
      }
    }
  }

  public markCallHandled(callId: string) {
    if (!callId) return;
    this.suppressedCallIds.set(callId, Date.now());
    this.pruneSuppressed();
  }

  private async checkForIncomingCalls(callback: Function) {
    try {
      // Wenn Telephony deaktiviert ist, keine API-Calls machen
      if (!environment.telephonyEnabled) {
        return;
      }
      
      this.pruneSuppressed();

      // Prüfe auf aktive eingehende Anrufe (API-Host aus Environment)
      const response = await fetch(`${environment.prismaApiUrl}/api/dispo/sipgate/active-calls`);
      if (response.ok) {
        const data = await response.json();
        
        // Wenn neue eingehende Anrufe vorhanden sind
        if (data.incoming_calls && data.incoming_calls.length > 0) {
          data.incoming_calls.forEach((call: any) => {
            const callId: string = call.id || call.callId || '';
            if (!callId) {
              return;
            }
            // Unterdrücke bereits behandelte IDs innerhalb TTL
            if (this.suppressedCallIds.has(callId)) {
              return;
            }
            // Jetzt anzeigen und gleichzeitig unterdrücken, damit es nicht erneut triggert
            this.suppressedCallIds.set(callId, Date.now());

            // Simuliere das Event-Format
            const event = {
              call: call,
              type: 'incoming_call'
            };
            
            console.log('Neuer eingehender Anruf gefunden:', callId);
            callback(event);
          });
        }
      }
    } catch (error) {
      console.log('Polling-Check fehlgeschlagen:', error);
    }
  }

  getEcho(): any {
    return this.echo;
  }

  isConnected(): boolean {
    return this.echo !== null;
  }

  // Cleanup
  disconnect() {
    this.stopPolling();
    this.echo = null;
  }
}
